/**
 * Mana Locals Admin — M01 tenant lifecycle boundary schemas.
 *
 * CONTRACT SOURCE: `contracts/openapi/foundation.v1.yaml` (API repository),
 * contract register v1.4. These four shapes are CONTRACT-BACKED — they mirror a
 * published API contract and must not be changed unilaterally. If the API
 * changes them, this file changes with it and the boundary test fails loudly
 * rather than a screen rendering `undefined`.
 *
 * ---------------------------------------------------------------------------
 * PROVISIONAL — CF-02 (wire casing) is DECISION_BLOCKED
 * ---------------------------------------------------------------------------
 * The API register records wire casing as an OPEN DECISION (CF-02) with
 * camelCase as the candidate form. camelCase is used here. If the API decides
 * snake_case, this file changes — and because every API type in the admin
 * derives from this file, nothing else does.
 *
 * ---------------------------------------------------------------------------
 * NOT BACKED BY A CONTRACT
 * ---------------------------------------------------------------------------
 * No list/paged tenant shape exists here, because the API exposes no tenant list
 * endpoint. That proposal lives in `tenants.provisional.ts`, clearly separated,
 * rather than being smuggled in beside contract-backed shapes where it could be
 * mistaken for something the API actually guarantees.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from '@/api/schemas/common';

/**
 * Lowercase alphanumeric, then alphanumeric or hyphen. Length 3-63.
 *
 * Mirrors the API's `TenantSlugSchema`, its OpenAPI `pattern`, and the database
 * `ck_tenant_slug_format` CHECK. The pattern PERMITS a trailing hyphen and
 * forbids only a leading one — that is deliberate on the API side and is
 * mirrored rather than "improved" here. Tightening it is an API-side migration,
 * not a frontend decision.
 */
export const TenantSlugSchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]{1,62}$/, 'Slug must be lowercase letters, digits or hyphens')
  .min(3)
  .max(63);

/**
 * Audit reason: 1-500 characters AFTER TRIM, containing at least one character
 * that is neither whitespace nor a Unicode control character.
 *
 * The bound applies to the trimmed value, so `.trim()` MUST run first. The same
 * single expression is used here as on the API side to keep the two aligned.
 */
const AUDIT_REASON_PATTERN = /^[^\p{Cc}]*[^\s\p{Cc}][^\p{Cc}]*$/u;

export const AuditReasonSchema = z
  .string()
  .trim()
  .min(1, 'A reason is required')
  .max(500, 'Reason must be 500 characters or fewer')
  .regex(AUDIT_REASON_PATTERN, 'Reason contains no printable characters');

/** Mirrors the database CHECK `status IN ('active','suspended','closed')`. */
export const TenantStatusSchema = z.enum(['active', 'suspended', 'closed']);

/**
 * Safe, non-disclosing error codes for the M01 slice.
 *
 * Note there is deliberately NO "tenant exists but you may not see it" code: a
 * tenant the caller may not see returns a non-disclosing 404 identical to a
 * missing tenant (M01-INV). The absence of a distinguishing code here is a
 * privacy property, not an omission.
 */
export const TenantErrorCodeSchema = z.enum([
  'INVALID_TENANT_SLUG',
  'INVALID_TENANT_PROFILE',
  'TENANT_SLUG_CONFLICT',
  'INVALID_STATUS_TRANSITION',
  'TENANT_NOT_FOUND',
  'TENANT_VERSION_CONFLICT',
  'IDEMPOTENCY_KEY_REQUIRED',
  'IDEMPOTENCY_KEY_REUSED',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'RATE_LIMITED',
  'SERVICE_UNAVAILABLE',
]);

export type TenantErrorCode = z.infer<typeof TenantErrorCodeSchema>;

/**
 * Nullable uuid. SHAPE ONLY.
 *
 * Legal-entity semantics are DECISION_BLOCKED upstream (D-C01 / OPEN-07). No
 * meaning is attached to this value anywhere in the admin: it is rendered as an
 * opaque identifier or not at all. Do not add validation, formatting or
 * interpretation for legal entities without an approved decision.
 */
export const LegalEntityIdSchema = UuidSchema.nullable();

/**
 * `POST /api/v1/tenants` request body.
 *
 * CONTRACT SOURCE: `contracts/openapi/foundation.v1.yaml` (API repository),
 * contract register v1.4, §3.1. Strict: unknown fields are rejected.
 *
 * `ownerAccountId` (optional): when provided, the API creates an initial owner
 * membership ATOMICALLY with the tenant. The account must exist and be verified.
 * This is the preferred way to establish ownership at creation time — the earlier
 * "owner self-onboards later" workaround is obsolete.
 *
 * Provisioning fields (all optional): `displayName` seeds the vendor_profile
 * business identity (defaults to the slug when omitted); `legalName`,
 * `contactEmail` and `contactPhone` seed the corresponding vendor_profile
 * columns; `contactName` is recorded on the audit row. Their grammar mirrors the
 * vendor_profile CHECK constraints, so a value the table would refuse is refused
 * here as `400 INVALID_TENANT_PROFILE`.
 *
 * `legalEntityId` is shape-only: a nullable uuid whose semantics are
 * DECISION_BLOCKED (register §3.1, OPEN-07). Omitted and explicit null both map
 * to SQL NULL.
 */
export const CreateTenantRequestSchema = z
  .object({
    slug: TenantSlugSchema,
    legalEntityId: LegalEntityIdSchema.optional(),
    ownerAccountId: UuidSchema.optional(),
    displayName: z.string().trim().min(1).max(200).optional(),
    legalName: z.string().trim().min(1).max(200).nullish(),
    contactName: z.string().trim().min(1).max(200).nullish(),
    contactEmail: z.string().email().max(320).nullish(),
    contactPhone: z
      .string()
      .trim()
      .regex(/^\+?[0-9][0-9 -]{5,31}$/, 'contactPhone must be a valid phone number')
      .nullish(),
  })
  .strict();

export type CreateTenantRequest = z.infer<typeof CreateTenantRequestSchema>;

/**
 * Only the `active -> suspended` edge is contracted. Other transitions are
 * DEFERRED upstream, not omitted, so this enum has exactly one member. Widening
 * it here would let the admin offer an action the API refuses.
 */
export const ChangeTenantStatusRequestSchema = z.object({
  status: z.literal('suspended'),
  reason: AuditReasonSchema,
});

export type ChangeTenantStatusRequest = z.infer<typeof ChangeTenantStatusRequestSchema>;

export const TenantSchema = z.object({
  id: UuidSchema,
  slug: TenantSlugSchema,
  status: TenantStatusSchema,
  legalEntityId: LegalEntityIdSchema,
  version: z.number().int().min(1),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});

export type Tenant = z.infer<typeof TenantSchema>;

/**
 * The shared success envelope.
 *
 * The API answers every M01 operation with `{ data, meta }` — the data member
 * carries the operation's subject and `meta` carries the correlation id. The
 * envelope is the shared CF-03 candidate and is parsed in ONE place.
 */
export const TenantSuccessResponseSchema = z.object({
  data: TenantSchema,
  meta: z.object({ requestId: z.string().min(1) }),
});

export type TenantSuccessResponse = z.infer<typeof TenantSuccessResponseSchema>;

/**
 * The platform's tenant REGISTRY — `GET /api/v1/tenants`.
 *
 * CONTRACT SOURCE: the API repository's `contracts/foundation.register.md` §2.3.
 *
 * This is a PLATFORM-scoped read: it lists every tenant of the deployment, so it
 * requires the `tenant.read` platform grant. It is not a tenant's own tenant list —
 * a tenant member holds no platform grant and receives `403`.
 *
 * The items are the SAME `TenantSchema` the create and suspend responses carry, so
 * a client that can read one tenant can read the collection without learning a
 * second vocabulary.
 */
export const TenantListResponseSchema = z.object({
  data: z.object({ tenants: z.array(TenantSchema) }),
  meta: z.object({ requestId: z.string().min(1) }),
});

export type TenantListResponse = z.infer<typeof TenantListResponseSchema>;

/**
 * True only for the one transition the API has implemented.
 *
 * Mirrors the API's `isSuspendEdgeLegal`. This mirrors rather than decides: the
 * database constraint remains authoritative, and the API re-checks. The frontend
 * uses this only to avoid offering an action that is known to be refused — never
 * to conclude that an action is permitted.
 */
export function isSuspendEdgeLegal(from: Tenant['status'], to: Tenant['status']): boolean {
  return (from === 'active' && to === 'suspended') || (from === 'suspended' && to === 'suspended');
}